/**
 * シフト変更の申請と承認(2026-09-15 MTG §6.1 → 2026-09-20 発注者指示で実装。D-113)。
 *
 * 「アポインターはシフトを**勝手に変更できない**。変更は **申請＋理由** を必須とし、**管理者の承認を経て確定**する」
 *  - 申請 1件 = `shift_change_requests/{id}`。1日ぶんの「こう変えたい」を1件で出す
 *  - 承認すると `shift_submissions/{month}_{op_id}.days[day]` を書き換え、変更履歴(logs)に残す
 *  - 却下は理由を添えて残す。どちらも申請は消さない(あとから見返せるように)
 * 出勤表(`shifts/{date}`)は管理者が作るものなので、この申請では触らない。
 */
import { addDoc, collection, doc, getDoc, getDocs, orderBy, query, serverTimestamp, updateDoc, where, writeBatch } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import type { MonthStr } from '@core/lib/types';
import { clearNoticeDay } from './shiftNoticeRepo';
import { dayHours, fetchSubmission, submissionData, submissionId, type ShiftChangeLog, type SubmitDay } from './shiftSubmitRepo';

export type ChangeStatus = 'pending' | 'approved' | 'rejected';

export const CHANGE_STATUS_LABELS: Record<ChangeStatus, string> = {
  pending: '承認待ち',
  rejected: '却下',
  approved: '承認済み',
};

/** 申請する内容。null = その日は休みにしたい */
export type ChangeWish = { start: number; end: number } | null;

export type ShiftChangeRequest = {
  id: string;
  month: MonthStr;
  /** 日(2桁。'05') */
  day: string;
  op_id: string;
  name: string;
  /** 出勤形態×報酬区分(提出と同じ) */
  bb_id: string;
  /** 申請したときの提出内容 */
  before: ChangeWish;
  /** 変更したい内容 */
  after: ChangeWish;
  /** 申請理由(必須) */
  reason: string;
  status: ChangeStatus;
  created_by: string;
  created_at?: unknown;
  /** 承認・却下したとき */
  decided_by?: string;
  decided_by_name?: string;
  decided_at?: unknown;
  decision_note?: string;
};

/** 「9:00〜18:00」「休み」のような文字にする(履歴・画面の表示に使う) */
export function wishLabel(w: ChangeWish): string {
  if (!w) return '休み';
  const hhmm = (h: number) => `${Math.floor(h)}:${h % 1 ? '30' : '00'}`;
  return `${hhmm(w.start)}〜${hhmm(w.end)}`;
}

const toWish = (d: SubmitDay | undefined): ChangeWish => (d ? { start: d.start, end: d.end } : null);

/** その月の申請(新しい順)。opId を渡すとその人の分だけ */
export async function fetchChangeRequests(month: MonthStr, opId?: string): Promise<ShiftChangeRequest[]> {
  const base = collection(getDb(), 'shift_change_requests');
  const q = opId
    ? query(base, where('month', '==', month), where('op_id', '==', opId), orderBy('created_at', 'desc'))
    : query(base, where('month', '==', month), orderBy('created_at', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ShiftChangeRequest, 'id'>) }));
}

/** 申請を出す。同じ日の「承認待ち」が残っていれば止める(二重申請を防ぐ) */
export async function createChangeRequest(args: {
  month: MonthStr;
  day: string;
  opId: string;
  name: string;
  bbId: string;
  after: ChangeWish;
  reason: string;
  userId: string;
}): Promise<string> {
  const { month, day, opId, name, bbId, after, reason, userId } = args;
  if (!reason.trim()) throw new Error('変更の理由を入力してください');
  if (after && after.end <= after.start) throw new Error('終了は開始より後の時刻にしてください');
  const sub = await fetchSubmission(month, opId);
  const before = toWish(sub?.days[day]);
  if (wishLabel(before) === wishLabel(after)) throw new Error('いまの提出と同じ内容です');

  const mine = await fetchChangeRequests(month, opId);
  if (mine.some((r) => r.day === day && r.status === 'pending')) throw new Error('この日はすでに承認待ちの申請があります');

  const ref = await addDoc(collection(getDb(), 'shift_change_requests'), {
    month,
    day,
    op_id: opId,
    name,
    bb_id: bbId || (sub?.bb_id ?? ''),
    before,
    after,
    reason: reason.trim(),
    status: 'pending' satisfies ChangeStatus,
    created_by: userId,
    created_at: serverTimestamp(),
  });
  return ref.id;
}

/** 申請を取り下げる(本人・承認待ちのときだけ) */
export async function cancelChangeRequest(id: string, userId: string): Promise<void> {
  await updateDoc(doc(getDb(), 'shift_change_requests', id), { status: 'rejected' satisfies ChangeStatus, decided_by: userId, decided_by_name: '本人が取り下げ', decided_at: serverTimestamp(), decision_note: '本人が取り下げました' });
}

/**
 * 承認する。シフト提出のその日を書き換え、変更履歴に残す。
 * 提出がまだ無い月は、その人の1件目として作る。
 * シフト提出・申請の状態・その日のお知らせ(管理者が直した分。本人が出した変更で上書きされるので消す。D-130)を1回で書く
 */
export async function approveChangeRequest(id: string, user: { id: string; name: string }, note = ''): Promise<void> {
  const ref = doc(getDb(), 'shift_change_requests', id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('申請が見つかりません');
  const r = { id: snap.id, ...(snap.data() as Omit<ShiftChangeRequest, 'id'>) };
  if (r.status !== 'pending') throw new Error('この申請はすでに処理済みです');

  const sub = await fetchSubmission(r.month, r.op_id);
  const days: Record<string, SubmitDay> = { ...(sub?.days ?? {}) };
  const before = toWish(days[r.day]);
  if (r.after) days[r.day] = { bb_id: r.bb_id || undefined, start: r.after.start, end: r.after.end };
  else delete days[r.day];

  const log: ShiftChangeLog = {
    day: r.day,
    before: wishLabel(before),
    // 変更前の時間(休憩・中抜けを引いた後)。差・本人成績の「提出」に使う(D-127)
    before_hours: dayHours(sub?.days[r.day]),
    after: wishLabel(r.after),
    by: `${user.name}(変更申請の承認)`,
    at: new Date().toISOString(),
  };
  const db = getDb();
  const batch = writeBatch(db);
  batch.set(
    doc(db, 'shift_submissions', submissionId(r.month, r.op_id)),
    submissionData({
      month: r.month,
      opId: r.op_id,
      name: sub?.name ?? r.name,
      bbId: sub?.bb_id ?? r.bb_id,
      days,
      note: sub?.note ?? '',
      userId: user.id,
      logs: [log, ...(sub?.logs ?? [])],
    }),
  );
  batch.update(ref, { status: 'approved' satisfies ChangeStatus, decided_by: user.id, decided_by_name: user.name, decided_at: serverTimestamp(), decision_note: note.trim() });
  await clearNoticeDay(batch, r.month, r.op_id, r.day);
  await batch.commit();
}

/** 却下する(理由を残す) */
export async function rejectChangeRequest(id: string, user: { id: string; name: string }, note: string): Promise<void> {
  if (!note.trim()) throw new Error('却下の理由を入力してください');
  const ref = doc(getDb(), 'shift_change_requests', id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('申請が見つかりません');
  if ((snap.data() as { status?: ChangeStatus }).status !== 'pending') throw new Error('この申請はすでに処理済みです');
  await updateDoc(ref, { status: 'rejected' satisfies ChangeStatus, decided_by: user.id, decided_by_name: user.name, decided_at: serverTimestamp(), decision_note: note.trim() });
}
